import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NbAuthOAuth2JWTToken, NbAuthResult, NbAuthService, NbAuthToken, NbTokenService } from '@nebular/auth';
import { Observable } from 'rxjs';
import moment from 'moment';
import * as CryptoJS from 'crypto-js';
import { BadgeNotificationWsService } from './ws/badge-notification-ws.service';

export enum IdleUserTimes {
  IdleTime = 28800000, // 8 hours
  CountdownTime = 5000
}

@Injectable()
export class AuthService {
  private timeoutId: any;
  private countdownId: any;
  private countdownValue: number;
  private logoutTimeout: any;
  private refreshTokenTimeout: any;
  private checkTokenInterval: any;
  private SECRET_KEY = 'mex.exchange.2024';

  constructor(
    private tokenService: NbTokenService,
    private nbAuthService: NbAuthService,
    private notificationWsService: BadgeNotificationWsService,
    private router: Router
  ) {
    this.initActivityListener();

    // Listens to logout fired from websocket attempt reconnection limit exceeded
    this.notificationWsService.getDisconnectionSubject().subscribe((disconnected: boolean) => {
      if (disconnected) {
        console.log('WebSocket reconnection attempts limit exceeded, logging out...');
        this.logout();
      }
    });
  }

  initActivityListener() {
    window.addEventListener('click', () => this.reset());

    // Checks periodically if there is no token to logout the user automatically in every browser tab
    this.checkTokenPresent({ queryParams: { returnUrl: window.location.pathname + window.location.search } });
  }

  checkTokenPresent(returnUrl?: any) {
    this.checkTokenInterval = setInterval(() => {
      if (!localStorage.getItem('auth_app_token') && !this.router.url.includes('/auth/')) {
        console.log('User logged out from browser');
        return this.logout(returnUrl);
      }
    }, 1500);
  }

  reset() {
    if (!this.router.url.includes('/auth/')) {
      clearTimeout(this.timeoutId);
      clearInterval(this.countdownId);
      this.startIdleTimer(IdleUserTimes.IdleTime);
      if (!this.checkTokenInterval) {
        this.checkTokenPresent();
      }
    }
  }

  startIdleTimer(timeout: number) {
    this.timeoutId = setTimeout(() => {
      console.log('Inactivity detected in current Tab');

      // Before logging the user out because of inactivity, we check if there is no activity in other tab
      const latestActivity = parseInt(localStorage.getItem('lastTimeActivity') || '1700000000');
      if (latestActivity <= Date.now() - timeout) {
        this.startCountdown();
      }
      else {
        clearTimeout(this.timeoutId);
        const newtTimeout = latestActivity + timeout - Date.now();
        console.log('There is a latest activity time registered in other tab at: ' + latestActivity);
        console.log('Reseting inactivity timeout to ' + newtTimeout + ' milisecons.');
        this.startIdleTimer(newtTimeout);
      }
    }, timeout);
    console.log("Idle timer set to timeout on " + timeout + "ms at " + Date.now().toString() + " (" + new Date() + ")");
    localStorage.setItem('lastTimeActivity', Date.now().toString());
  }

  startCountdown() {
    this.countdownValue = IdleUserTimes.CountdownTime / 1000;
    this.countdownId = setInterval(() => {
      this.countdownValue--;
      console.log('You will logout in:', this.countdownValue, 'seconds');
      if (this.countdownValue <= 0) {
        clearTimeout(this.timeoutId);
        clearInterval(this.countdownId);
        console.log('User idle, logging out...');
        this.logout();
      }
    }, 1000);
  }

  onTokenChange(): Observable<NbAuthToken> {
    this.reset();
    return this.nbAuthService.onTokenChange()
  }

  refreshToken(): Observable<NbAuthResult> {
    const token = localStorage.getItem('auth_app_token') ? JSON.parse(localStorage.getItem('auth_app_token')).value : '';
    if (token) {
      const refresh_token = JSON.parse(token).refresh_token;
      return this.nbAuthService.refreshToken('email', { token: refresh_token });
    }
  }

  logout(navigationExtras?: any) {
    this.tokenService.clear();
    clearInterval(this.checkTokenInterval);
    this.checkTokenInterval = null;
    clearTimeout(this.logoutTimeout);
    clearTimeout(this.refreshTokenTimeout);
    this.clearStorageDateFilters();
    this.router.navigate(['/auth/login']);
    navigationExtras ? this.router.navigate(['/auth/login'], navigationExtras) : this.router.navigate(['/auth/login']);
  }

  clearStorageDateFilters() {
    let dateFiltersToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.endsWith('-startDate-filter') || localStorage.key(i)?.endsWith('-endDate-filter')) {
        dateFiltersToRemove.push(localStorage.key(i));
      }
    }
    for (let i = 0; i < dateFiltersToRemove.length; i++) {
      if (dateFiltersToRemove[i]) {
        localStorage.removeItem(dateFiltersToRemove[i] || '');
      }
    }
  }

  setExpirationTokenTimeout(token: NbAuthOAuth2JWTToken) {
    // the timeout will automatically be triggered when the token expires
    if (token && token.getAccessTokenPayload()) {
      const expirationTimestamp = token.getAccessTokenPayload().exp;
      if (expirationTimestamp) {
        const timeout = (expirationTimestamp - moment().unix()) * 1000;
        clearTimeout(this.logoutTimeout);
        this.logoutTimeout = setTimeout( () => {
          console.log('JWT Token expired');
          this.logout();
        }, timeout);

        // each 10 minutes, the token will be refreshed if user is active
        clearTimeout(this.refreshTokenTimeout);
        const refreshTimeout = 600000;
        this.refreshTokenTimeout = setTimeout( () => {
          this.refreshToken().subscribe((result: NbAuthResult) => {
            result?.isSuccess() ? console.log('User JWT Token refreshed.') : console.log('An error occurred when trying to refresh JWT Token.')
          });
        }, refreshTimeout);
      }
    }
  }

  isAuthenticated() {
    return this.nbAuthService.isAuthenticated();
  }

  isAuthorized(requiredGroups: string[] | undefined): boolean {
    if (requiredGroups === undefined || requiredGroups.length === 0) {
      return true;
    }

    // Check to see if there are required groups not being satisfied by the user
    const difference = requiredGroups.filter((group) => this.userGroups.indexOf(group) === -1);
    return difference.length < requiredGroups.length;
  }

  set userGroups(userGroups: string[]) {
    const encryptedGroups = CryptoJS.AES.encrypt(JSON.stringify(userGroups), this.SECRET_KEY).toString();
    localStorage.setItem('userGroups', encryptedGroups);
  }

  get userGroups(): string[] {
    const userGroups = localStorage.getItem('userGroups');
    if (userGroups) {
      const decryptedGroups = CryptoJS.AES.decrypt(userGroups, this.SECRET_KEY);
      return JSON.parse(decryptedGroups.toString(CryptoJS.enc.Utf8));
    } else {
      return [];
    }
  }
}
